import axios from 'axios';
import { config } from 'shared/config';
import { primeSharedResource, readSharedResource } from 'shared/api/readResource';
import { prefetchPageShellManifest, requestInitialPageShellManifest, requestPageShellManifest } from 'features/visuals/public/data/pageShellManifest';
import { prefetchHtmlPage } from 'features/html-reporting/public/prefetch';
import type { LockedViewportSettings } from './page/ReportWorkspaceSupport';
import type { Connection } from 'features/connections/public/model/types';

export type WorkspaceMetadata = {
    reportId: string;
    reportName: string;
    hasPermission: true;
    /** `Pages`: the reader holds page grants and no report access (`scope: 'pages'`), so `pages` lists only their pages. */
    accessLevel: string;
    pages: { PageID: number; PageUID: string; PageName: string; PageKind?: 'report' | 'html'; Hidden?: boolean; accessLevel?: 'View' | 'EditContent' | 'Edit'; lockedViewport?: Partial<LockedViewportSettings> }[];
    connections: Connection[];
};

export const reportModelContext = (connections: Connection[]): Connection | null => (
    connections.length ? {
        id: Math.min(...connections.map(source => source.id)),
        name: 'Report data model', serverType: 'DuckDB', serverName: '', databaseName: '',
        connectionType: 'ReportModel',
    } : null
);

// Kept for older imports; this is the full model context, never a selected source.
export const activeReportConnection = reportModelContext;


const metadataKey = (reportId: string, userId: string) => `report-workspace:metadata:${reportId}:${userId}`;

async function requestWorkspaceMetadata(reportId: string, userId: string): Promise<WorkspaceMetadata> {
    const { data } = await axios.get<WorkspaceMetadata>(`${config.API_URL}/api/reports/${reportId}/workspace`, {
        params: { userId },
    });
    if (data.reportId !== reportId || data.hasPermission !== true
        || !Array.isArray(data.pages) || !Array.isArray(data.connections)
        || !['Owner', 'Admin', 'Edit', 'View', 'Viewer', 'Pages'].includes(data.accessLevel)) {
        throw new Error('The report service returned incomplete workspace metadata.');
    }
    return data;
}

export async function loadWorkspaceMetadata(reportId: string, userId: string): Promise<WorkspaceMetadata> {
    return readSharedResource(metadataKey(reportId, userId), () => requestWorkspaceMetadata(reportId, userId));
}

// The page last open in this tab, if it belongs to this report (ActivePageContext stores both).
const rememberedPageId = (reportId?: string): number | null => {
    try {
        if (reportId && sessionStorage.getItem('reportId')?.toLowerCase() !== reportId.toLowerCase()) return null;
        const remembered: unknown = JSON.parse(sessionStorage.getItem('activePageId') || 'null');
        return typeof remembered === 'number' ? remembered : null;
    } catch {
        return null;
    }
};

/**
 * The page the workspace opens first: the page linked in the URL (?page= uid or id),
 * else the remembered page when it still exists, else the first page.
 */
export const initialWorkspacePage = (
    pages: WorkspaceMetadata['pages'],
    linkedPage?: string | null,
    reportId?: string,
): WorkspaceMetadata['pages'][number] | undefined => {
    const wanted = linkedPage?.toLowerCase();
    const linked = wanted ? pages.find(page => page.PageUID?.toLowerCase() === wanted || String(page.PageID) === wanted) : undefined;
    if (linked) return linked;
    const remembered = rememberedPageId(reportId);
    return pages.find(page => page.PageID === remembered) ?? pages[0];
};

type ManifestPage = { page?: { pageId?: number; pageUid?: string; pageLayout?: { kind?: string } } };

/**
 * Report open: start the workspace read as soon as the signed-in user is known
 * (in parallel with the route's own checks and code loading) and, in parallel
 * with it, the shell manifest of the page the workspace will most likely open:
 * the page linked in the URL, else the remembered page, else the report's first
 * visible page. Once the workspace names the page, that early read is used only
 * if it read that page; otherwise the page's manifest is read then. An HTML
 * page's document and data reads start as soon as either read names it. The
 * workspace and canvas take these reads once; nothing is cached beyond that.
 */
export function prefetchReportOpen(reportId: string, userId: string, linkedPage?: string | null): void {
    const remembered = rememberedPageId(reportId);
    const early = linkedPage ? requestPageShellManifest<ManifestPage>(reportId, linkedPage, userId)
        : remembered !== null ? requestPageShellManifest<ManifestPage>(reportId, remembered, userId)
        : requestInitialPageShellManifest<ManifestPage>(reportId, userId);
    early.then(response => {
        const page = response.data?.page;
        if (page?.pageLayout?.kind === 'html' && page.pageUid) prefetchHtmlPage(reportId, page.pageUid);
    }).catch(() => undefined);
    void primeSharedResource(metadataKey(reportId, userId), () => requestWorkspaceMetadata(reportId, userId))
        .then(metadata => {
            const page = initialWorkspacePage(metadata.pages, linkedPage, reportId);
            if (!page) return;
            if (page.PageKind === 'html') {
                prefetchHtmlPage(reportId, page.PageUID);
                return;
            }
            const pending = early
                .then(response => (response.data?.page?.pageId === page.PageID
                    ? response
                    : requestPageShellManifest(reportId, page.PageID, userId)))
                .catch(() => requestPageShellManifest(reportId, page.PageID, userId));
            prefetchPageShellManifest({ reportId, pageId: page.PageID, userId }, pending);
        })
        .catch(() => undefined);
}

export async function loadReportConnections(reportId: string, userId: string): Promise<Connection[]> {
    return readSharedResource(`connections:report:${reportId}:${userId}`, async () => {
        const { data } = await axios.get<Connection[]>(`${config.API_URL}/api/connections/report/${reportId}`, {
            params: { userId },
        });
        return data;
    });
}
